import React, { useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import toast from 'react-hot-toast';
import { X, User, Phone, Shield, CheckCircle, XCircle, Trash2, Unlock, Edit, MessageSquare, Bell, RefreshCw, Zap } from 'lucide-react';
import { trackEvent } from './lib/analytics';

const API_BASE_URL = '/api';

// Transactions whose GA4 `refund` event has already fired in this page session.
// Belt-and-suspenders on top of the server's authoritative `newlyRefunded` flag, so a
// double invocation within one session can't double-fire before the flag round-trips.
const refundTracked = new Set();

const ist = (t) => moment(t).utcOffset(330);

const STATUS_STYLES = {
    completed: 'bg-green-100 text-green-700',
    confirmed: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-red-100 text-red-700',
    failed: 'bg-red-100 text-red-700',
    blocked: 'bg-blue-100 text-blue-700'
};

// Shared booking-details dialog used by both the Dashboard (incl. Photoshoots view)
// and the Calendar Manager. Self-contained: performs its own API calls and reports
// mutations back through onUpdated so the parent can refresh its data.
const BookingDetailsModal = ({ booking, onClose, onUpdated }) => {
    const [editing, setEditing] = useState(false);
    const [editData, setEditData] = useState({ user_name: '', user_email: '', phone: '' });
    const [cancelling, setCancelling] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [current, setCurrent] = useState(null);

    if (!booking) return null;
    const b = current || booking;
    const isBlockedSlot = b.payment_status === 'blocked' || b._isBlockedSlot;
    const amount = parseFloat(b.total_amount) > 0 ? b.total_amount : (b.amount || 0);
    const refundAmount = parseFloat(b.refund_amount);
    const txnid = b.payu_txnid || b.razorpay_order_id;

    const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

    const finish = (updatedBooking) => {
        if (updatedBooking) setCurrent(updatedBooking);
        if (onUpdated) onUpdated();
    };

    const close = () => {
        setEditing(false);
        setCancelling(false);
        setCancelReason('');
        setCurrent(null);
        onClose();
    };

    const startEdit = () => {
        setEditData({ user_name: b.user_name || '', user_email: b.user_email || '', phone: b.phone || '' });
        setCancelling(false);
        setEditing(true);
    };

    const saveEdit = async () => {
        setBusy(true);
        try {
            const { data } = await axios.patch(`${API_BASE_URL}/admin/bookings/${b.id}`, editData, { headers: authHeaders() });
            if (data.success) {
                toast.success('Booking details updated');
                setEditing(false);
                finish(data.booking);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update booking details');
        } finally {
            setBusy(false);
        }
    };

    const confirmCancel = async () => {
        if (!cancelReason.trim()) { toast.error('Please enter a cancellation reason'); return; }
        setBusy(true);
        try {
            await axios.delete(`${API_BASE_URL}/admin/bookings/${b.id}`, {
                headers: authHeaders(),
                data: { reason: cancelReason.trim() }
            });
            toast.success('Booking cancelled');
            finish();
            close();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to cancel booking');
        } finally {
            setBusy(false);
        }
    };

    const confirmPayment = async () => {
        setBusy(true);
        try {
            const { data } = await axios.patch(`${API_BASE_URL}/admin/bookings/${b.id}/status`, { status: 'confirmed' }, { headers: authHeaders() });
            if (data.success) {
                toast.success('Payment confirmed');
                finish(data.booking);
            }
        } catch (error) {
            toast.error('Failed to confirm payment');
        } finally {
            setBusy(false);
        }
    };

    const freeSlot = async () => {
        if (!window.confirm(`Free this slot? This clears the ${b.payment_status} hold so the slot can be booked again. The customer will not be notified.`)) return;
        setBusy(true);
        try {
            await axios.post(`${API_BASE_URL}/admin/bookings/${b.id}/free-slot`, {}, { headers: authHeaders() });
            toast.success('Slot freed for re-booking');
            finish();
            close();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to free slot');
        } finally {
            setBusy(false);
        }
    };

    const deleteBooking = async () => {
        if (!window.confirm('Permanently delete this booking? This cannot be undone.')) return;
        setBusy(true);
        try {
            await axios.delete(`${API_BASE_URL}/admin/bookings/${b.id}/hard`, { headers: authHeaders() });
            toast.success('Booking deleted permanently');
            finish();
            close();
        } catch (error) {
            toast.error('Failed to delete booking');
        } finally {
            setBusy(false);
        }
    };

    const resendWhatsApp = async () => {
        if (!b.phone) { toast.error('This booking has no phone number.'); return; }
        if (!window.confirm(`Resend the confirmation WhatsApp to ${b.user_name} (${b.phone})?`)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/admin/bookings/${b.id}/resend-whatsapp`, {}, { headers: authHeaders() });
            if (data.success) toast.success(data.message || 'WhatsApp resent');
            else toast.error(data.error || 'Failed to resend WhatsApp');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to resend WhatsApp');
        } finally {
            setBusy(false);
        }
    };

    const resendCaretaker = async () => {
        const kind = b.payment_status === 'cancelled' ? 'cancellation' : 'booking';
        if (!window.confirm(`Resend the caretaker's ${kind} WhatsApp alert for ${b.user_name}?`)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/admin/bookings/${b.id}/resend-caretaker`, {}, { headers: authHeaders() });
            if (data.success) toast.success(data.message || 'Caretaker alert resent');
            else toast.error(data.error || 'Failed to resend caretaker alert');
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to resend caretaker alert');
        } finally {
            setBusy(false);
        }
    };

    const recheckPayment = async () => {
        if (!window.confirm(`Re-check this payment with PayU for ${b.user_name}? If PayU reports it paid, the booking will be confirmed and the customer notified.`)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API_BASE_URL}/admin/bookings/${b.id}/recheck-payment`, {}, { headers: authHeaders() });
            if (data.success) toast.success(data.message || `Status: ${data.status}`);
            else toast.error(data.message || data.error || 'Could not verify payment');

            // Offset the original GA4 `purchase` revenue when PayU reports a refund. Same
            // transaction_id + currency as the purchase (PaymentSuccess.jsx) so GA4 nets them.
            // Fire once per transaction: server `newlyRefunded` is authoritative, session Set guards reruns.
            if (data.success && data.status === 'refunded' && data.newlyRefunded && b.payu_txnid && !refundTracked.has(b.payu_txnid)) {
                refundTracked.add(b.payu_txnid);
                trackEvent('refund', {
                    transaction_id: b.payu_txnid,
                    currency: 'INR',
                    value: data.refundAmount,
                });
            }

            finish(data.booking);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to re-check payment');
        } finally {
            setBusy(false);
        }
    };

    const unblockSlot = async () => {
        if (!window.confirm('Unblock this slot?')) return;
        setBusy(true);
        try {
            await axios.delete(`${API_BASE_URL}/admin/block-slot/${b._blockedSlotId}`, { headers: authHeaders() });
            toast.success('Slot unblocked');
            finish();
            close();
        } catch (error) {
            toast.error('Failed to unblock slot');
        } finally {
            setBusy(false);
        }
    };

    const infoTile = (label, value) => value ? (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{label}</div>
            <div className="text-sm font-medium text-gray-800 mt-0.5">{value}</div>
        </div>
    ) : null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[92vh] flex flex-col">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <h3 className="text-xl font-bold text-gray-800">Booking Details</h3>
                    <button onClick={close} className="p-2 hover:bg-white rounded-full transition-colors text-gray-500 hover:text-gray-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6 overflow-y-auto">
                    {/* Status Badge */}
                    <div className="flex justify-center">
                        <span className={`px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wide ${STATUS_STYLES[b.payment_status] || 'bg-gray-100 text-gray-700'}`}>
                            {b.payment_status}
                        </span>
                    </div>

                    {/* User Info */}
                    <div className="space-y-3">
                        <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider">User Information</h4>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            {editing ? (
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Name</label>
                                        <input type="text" value={editData.user_name}
                                            onChange={(e) => setEditData(p => ({ ...p, user_name: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Email</label>
                                        <input type="email" value={editData.user_email}
                                            onChange={(e) => setEditData(p => ({ ...p, user_email: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Phone (WhatsApp updates)</label>
                                        <input type="tel" value={editData.phone}
                                            onChange={(e) => setEditData(p => ({ ...p, phone: e.target.value }))}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                            <User className="w-5 h-5" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-bold text-gray-800 truncate">{b.user_name}</div>
                                            <div className="text-sm text-gray-500 truncate">{b.user_email}</div>
                                        </div>
                                    </div>
                                    {b.phone && (
                                        <div className="flex items-center gap-2 text-gray-600 text-sm pl-1">
                                            <Phone className="w-4 h-4 text-gray-400" /> {b.phone}
                                        </div>
                                    )}
                                </>
                            )}
                            {b.id_proof_url && !editing && (
                                <div className="pt-2 border-t border-gray-200 mt-2">
                                    <a href={b.id_proof_url} target="_blank" rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline text-sm flex items-center gap-1 font-medium">
                                        <Shield className="w-3 h-3" /> View ID Proof
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Slot + Payment */}
                    {!isBlockedSlot && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Slot Details</h4>
                                <div className="p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                                    <div className="font-bold text-blue-900">{b.slot_id || (b.booking_type || '').toUpperCase() || '—'}</div>
                                    <div className="text-xs text-blue-700">{ist(b.slot_time).format('MMM DD, YYYY')}</div>
                                    <div className="text-xs text-blue-600/70 mt-1">
                                        {ist(b.slot_time).format('hh:mm A')} - {ist(b.slot_time).add(parseFloat(b.duration) || 0, 'hours').format('hh:mm A')}
                                    </div>
                                    {b.duration_label && <div className="text-xs text-blue-600/70 mt-1">{b.duration_label}</div>}
                                </div>
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Payment</h4>
                                <div className="p-3 bg-green-50/50 rounded-lg border border-green-100">
                                    <div className="font-bold text-green-900">₹{Number(amount).toLocaleString()}</div>
                                    {b.deposit > 0 && <div className="text-xs text-green-700">Deposit: ₹{Number(b.deposit).toLocaleString()}</div>}
                                    <div className="text-xs text-green-700 truncate" title={txnid}>Tx: {txnid || 'N/A'}</div>
                                    <div className="text-xs text-green-600/70 mt-1">{ist(b.created_at).format('MMM DD, HH:mm')}</div>
                                    {refundAmount > 0 && (
                                        <div className="text-xs font-bold text-orange-600 mt-1">
                                            Refunded ₹{refundAmount.toLocaleString()}{b.refunded_at ? ` on ${ist(b.refunded_at).format('MMM DD, YYYY')}` : ''}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Extra details */}
                    {!isBlockedSlot && (
                        <div className="grid grid-cols-2 gap-3">
                            {infoTile('Booking Type', b.booking_type)}
                            {infoTile('Duration', b.duration_label || b.duration)}
                            {infoTile('Shoot Type', b.shoot_type)}
                            {infoTile('Event Type', b.event_type)}
                            {infoTile('Guest Count', b.guest_count)}
                            {infoTile('Decor Size', b.decor_size)}
                            {infoTile('Guest Use Time', b.guest_use_time)}
                            {infoTile('Buffer Period', b.buffer_after)}
                            {b.housekeeping_fee > 0 && infoTile('Housekeeping Fee', `₹${Number(b.housekeeping_fee).toLocaleString()}`)}
                            {infoTile('Date of Birth', b.date_of_birth ? ist(b.date_of_birth).format('MMM DD, YYYY') : null)}
                            {b.cancellation_reason && (
                                <div className="col-span-2 p-3 bg-red-50/50 rounded-lg border border-red-100">
                                    <div className="text-[10px] font-bold text-red-400 uppercase tracking-wider">Cancellation Reason</div>
                                    <div className="text-sm text-red-800 mt-0.5">{b.cancellation_reason}</div>
                                </div>
                            )}
                            {b.special_requirements && (
                                <div className="col-span-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Special Requirements</div>
                                    <div className="text-sm text-gray-800 mt-0.5">{b.special_requirements}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Record & payment identifiers */}
                    {!isBlockedSlot && (
                        <div className="space-y-3">
                            <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Record Info</h4>
                            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                                <div className="flex justify-between gap-4">
                                    <span className="text-gray-400 font-medium shrink-0">Booking ID</span>
                                    <span className="font-mono text-xs text-gray-700 break-all text-right">{b.id}</span>
                                </div>
                                {b.payu_txnid && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">PayU Txn ID</span>
                                        <span className="font-mono text-xs text-gray-700 break-all text-right">{b.payu_txnid}</span>
                                    </div>
                                )}
                                {b.payu_payment_id && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">PayU Payment ID</span>
                                        <span className="font-mono text-xs text-gray-700 break-all text-right">{b.payu_payment_id}</span>
                                    </div>
                                )}
                                {b.razorpay_order_id && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">Order ID</span>
                                        <span className="font-mono text-xs text-gray-700 break-all text-right">{b.razorpay_order_id}</span>
                                    </div>
                                )}
                                <div className="flex justify-between gap-4">
                                    <span className="text-gray-400 font-medium shrink-0">Created At</span>
                                    <span className="text-gray-700 text-right">{ist(b.created_at).format('MMM DD, YYYY hh:mm A')}</span>
                                </div>
                                {b.updated_at && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">Updated At</span>
                                        <span className="text-gray-700 text-right">{ist(b.updated_at).format('MMM DD, YYYY hh:mm A')}</span>
                                    </div>
                                )}
                                {b.payment_completed_at && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">Paid At</span>
                                        <span className="text-gray-700 text-right">{ist(b.payment_completed_at).format('MMM DD, YYYY hh:mm A')}</span>
                                    </div>
                                )}
                                {b.payment_error && (
                                    <div className="flex justify-between gap-4">
                                        <span className="text-gray-400 font-medium shrink-0">Payment Error</span>
                                        <span className="text-red-600 text-xs text-right break-all">{b.payment_error}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Cancel reason input */}
                    {cancelling && (
                        <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                            <label className="text-xs font-bold text-red-500 uppercase tracking-wider">Cancellation Reason</label>
                            <textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="Why is this booking being cancelled?"
                                className="mt-2 w-full border border-red-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400 focus:border-red-400 bg-white resize-none"
                                rows={2}
                                autoFocus
                            />
                        </div>
                    )}
                </div>

                {/* Actions */}
                <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-wrap gap-3">
                    {editing ? (
                        <>
                            <button onClick={saveEdit} disabled={busy}
                                className="flex-1 py-2.5 px-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-md shadow-green-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                <CheckCircle className="w-4 h-4" /> {busy ? 'Saving…' : 'Save Changes'}
                            </button>
                            <button onClick={() => setEditing(false)} disabled={busy}
                                className="flex-1 py-2.5 px-4 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-sm text-sm">
                                Discard
                            </button>
                        </>
                    ) : cancelling ? (
                        <>
                            <button onClick={confirmCancel} disabled={busy}
                                className="flex-1 py-2.5 px-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-md shadow-red-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                <XCircle className="w-4 h-4" /> {busy ? 'Cancelling…' : 'Confirm Cancellation'}
                            </button>
                            <button onClick={() => { setCancelling(false); setCancelReason(''); }} disabled={busy}
                                className="flex-1 py-2.5 px-4 bg-white border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-sm text-sm">
                                Keep Booking
                            </button>
                        </>
                    ) : isBlockedSlot ? (
                        <button onClick={unblockSlot} disabled={busy}
                            className="flex-1 py-2.5 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-md shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                            <Unlock className="w-4 h-4" /> Unblock Slot
                        </button>
                    ) : (
                        <>
                            <div className="w-full grid grid-cols-2 gap-3">
                                <button onClick={resendWhatsApp} disabled={busy}
                                    className="py-2.5 px-4 bg-white border border-emerald-200 text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm">
                                    <MessageSquare className="w-4 h-4" /> Resend WhatsApp
                                </button>
                                <button onClick={resendCaretaker} disabled={busy}
                                    className="py-2.5 px-4 bg-white border border-amber-200 text-amber-600 font-semibold rounded-xl hover:bg-amber-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm">
                                    <Bell className="w-4 h-4" /> Resend Caretaker Alert
                                </button>
                            </div>
                            {/* cancelled included so a PayU refund gets detected and recorded */}
                            {['pending', 'failed', 'cancelled'].includes(b.payment_status) && (
                                <button onClick={recheckPayment} disabled={busy}
                                    className="w-full py-2.5 px-4 bg-white border border-purple-200 text-purple-600 font-semibold rounded-xl hover:bg-purple-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm">
                                    <RefreshCw className="w-4 h-4" /> Re-check Payment with PayU
                                </button>
                            )}
                            {['pending', 'failed'].includes(b.payment_status) && (
                                <button onClick={freeSlot} disabled={busy}
                                    className="w-full py-2.5 px-4 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors shadow-md shadow-amber-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                                    title="Clears this hold without notifying the customer, so the slot can be booked again">
                                    <Zap className="w-4 h-4" /> {busy ? 'Freeing…' : 'Free This Slot'}
                                </button>
                            )}
                            <button onClick={startEdit} disabled={busy}
                                className="flex-1 min-w-[45%] py-2.5 px-4 bg-white border border-blue-200 text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm">
                                <Edit className="w-4 h-4" /> Edit Details
                            </button>
                            {b.payment_status === 'pending' && (
                                <button onClick={confirmPayment} disabled={busy}
                                    className="flex-1 min-w-[45%] py-2.5 px-4 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors shadow-md shadow-green-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                    <CheckCircle className="w-4 h-4" /> Confirm Payment
                                </button>
                            )}
                            {b.payment_status !== 'cancelled' && (
                                <button onClick={() => setCancelling(true)} disabled={busy}
                                    className="flex-1 min-w-[45%] py-2.5 px-4 bg-white border border-red-200 text-red-600 font-semibold rounded-xl hover:bg-red-50 transition-colors shadow-sm flex items-center justify-center gap-2 text-sm">
                                    <XCircle className="w-4 h-4" /> Cancel Booking
                                </button>
                            )}
                            <button onClick={deleteBooking} disabled={busy}
                                className="flex-1 min-w-[45%] py-2.5 px-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-md shadow-red-200 disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                <Trash2 className="w-4 h-4" /> Delete Permanently
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingDetailsModal;
